import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";

import { useProductFeatureFlag } from "../hooks/useFeatureFlag";
import { AppStrings } from "../utils/constants/AppStrings";
import GuardFallbackRedirect from "./GuardFallbackRedirect";

export type ProductFeatureFlagGuardProps = {
  children: ReactNode;
  /** TCM V2 `products.*.feature_flags` entry (e.g. `transactions_routing`). */
  flag: string;
  /**
   * When `true` (default), children render only if the flag is on.
   * When `false`, children render only if the flag is off — used for the
   * legacy Withdrawal Review V1 routes while the V2 engine is active.
   */
  requireEnabled?: boolean;
  /**
   * Explicit redirect when the gate fails (route mode). Prefer the sibling
   * WR version so admins land on the live engine instead of a random page.
   */
  redirectTo?: string;
  mode?: "route" | "conditional";
};

/**
 * Route/UI gate for TCM V2 product `feature_flags` entries.
 *
 * Distinct from `<FeatureFlagGuard feature=...>` which reads v1
 * `features_status_config` via `FEATURE_MAPPINGS`. Product feature flags
 * live under `/admin-panel/platform/v2/config` → `products.*.feature_flags`
 * and are checked with `useProductFeatureFlag`.
 *
 * Fail-safe on fetch errors: `useProductFeatureFlag` resolves to
 * `{ isEnabled: false, loading: false, error }` when initialize fails
 * (network/timeout). This guard intentionally keys only on `isEnabled` —
 * same fail-closed default as `FeatureFlagGuard` — so a transient error
 * behaves like the flag being off: `requireEnabled=true` redirects away
 * from the flagged page (e.g. WR V2 → V1); `requireEnabled=false` keeps
 * the legacy page (e.g. WR V1) reachable. Prefer a known-good engine over
 * blocking both routes behind an error screen.
 */
export default function ProductFeatureFlagGuard({
  children,
  flag,
  requireEnabled = true,
  redirectTo,
  mode = "route",
}: ProductFeatureFlagGuardProps): ReactNode {
  // `error` is intentionally unused — see fail-safe note in the JSDoc above.
  const { isEnabled, loading } = useProductFeatureFlag(flag);

  if (loading) {
    return mode === "conditional" ? null : (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div>{AppStrings.PAM_LOADING}</div>
      </div>
    );
  }

  const allowed = requireEnabled ? isEnabled : !isEnabled;

  if (!allowed) {
    if (mode === "conditional") {
      return null;
    }
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }
    return <GuardFallbackRedirect />;
  }

  return children;
}
